import * as i18n from "i18next";
import { useQuery } from "@apollo/client";
import { DSBreakpoints, DSPalette, DSTypography } from "@clickbank-ui/seller-design-system";
import {
	Close,
	EmailOutlined,
	LocationOnOutlined,
	PhoneOutlined,
	ScreenShareOutlined,
	WarningAmber,
	WebAssetOutlined
} from "@mui/icons-material";
import {
	Box,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	IconButton,
	Link,
	Typography
} from "@mui/material";
import { Stack } from "@mui/system";
import PropTypes from "prop-types";
import { useState } from "react";
import { Trans } from "react-i18next";

import { SITE_SETTINGS } from "../../Api";
import CopyableLink from "../CopyableLink/CopyableLink";

const breakpoints = DSBreakpoints.default;
const { cbNeutral, palette } = DSPalette;
const { primaryFont } = DSTypography;

const styles = {
	linkColumn: {
		"& .MuiStack-root": {
			display: "flex",
			flexDirection: "column",
			alignContent: "center",

			"&.email": {
				flexDirection: "row",
				alignItems: "center"
			}
		},
		"& .MuiLink-root": {
			color: palette.primary.main,

			"&.red": {
				color: palette.error.main,
				textDecorationColor: palette.error.main
			},
			"&:hover": {
				textDecoration: "none"
			}
		}
	},
	doubleLine: {
		marginBottom: "16px",
		wordBreak: "break-all",
		lineHeight: 1.1
	},
	header: {
		fontWeight: "600",
		color: palette.primary.main,
		marginBottom: "5px"
	},
	mobileViewMargin: {
		[breakpoints.down("sm")]: {
			marginTop: "24px",
			flexDirection: "column"
		}
	},
	phone: {
		margin: "24px 0 0",
		flexDirection: "row",
		paddingRight: "15px",

		[breakpoints.down("sm")]: {
			margin: "16px 0 0",
			flexDirection: "column",
			paddingRight: "0"
		}
	},
	returnContainer: {
		padding: "0 2rem 0 0",

		[breakpoints.up("lg")]: {
			padding: "0 3rem 0 0"
		}
	},
	addressDialogContent: {
		position: "relative",

		[breakpoints.up("xs")]: {
			minWidth: 350
		},
		"& a": {
			wordBreak: "break-word"
		},
		"& .MuiTypography-root": {
			color: cbNeutral[400]
		}
	},
	returnInstructions: {
		"& pre": {
			margin: 0,
			fontFamily: primaryFont,
			fontSize: "1rem",
			color: cbNeutral[400],
			whiteSpace: "pre-line"
		}
	},
	sellerAndCustomerInfoHeading: {
		margin: "24px 0",

		[breakpoints.down("sm")]: {
			margin: "16px 0 0"
		}
	}
};

const SellerInformation = ({ lineItems, pitchPage }) => {
	const [addressModalOpen, setAddressModalOpen] = useState(false);

	const handleAddressModal = () => setAddressModalOpen(!addressModalOpen);

	const vendorId =
		lineItems && lineItems?.length > 0 ? lineItems[0]?.product?.vendor?.nickname : undefined;

	const { data: siteSettingsData } = useQuery(SITE_SETTINGS, {
		variables: { vendorId },
		skip: !vendorId,
		context: {
			noAuth: true
		}
	});
	const customerService = siteSettingsData?.siteSettings?.customerService;
	const address1 = customerService?.address1;
	const address2 = customerService?.address2;
	const state = customerService?.state;
	const countryCode = customerService?.countryCode;
	const residential = customerService?.residential;

	const uniqueArray = lineItems?.filter((item, index) => {
		const _item = JSON.stringify(item.product.vendor);
		return (
			index ===
			lineItems.findIndex(obj => {
				return JSON.stringify(obj.product.vendor) === _item;
			})
		);
	});

	return (
		<div>
			<Divider />
			<Typography variant="h3" sx={styles.sellerAndCustomerInfoHeading}>
				<Trans i18nKey="OrderDetails.sellerInfo">Seller Information</Trans>
			</Typography>

			{uniqueArray?.map((value, index) => {
				const vendor = value?.product?.vendor;

				return (
					<Grid
						container
						key={index}
						columnSpacing={{ xs: 0, md: 8 }}
						rowSpacing={{ xs: 2, sm: 4, md: 0 }}
					>
						<Grid item xs={12} md={6} lg={7}>
							<Typography
								variant="h3"
								as="h4"
								sx={{
									paddingBottom: "16px",
									fontWeight: "400",
									color: palette.primary.main
								}}
								data-cy="seller-nickname"
							>
								{vendor?.brandName}
							</Typography>
							<Grid
								item
								xs={12}
								lg={
									value?.product?.shippable && value?.product?.returnInformation
										? 4
										: 12
								}
								sx={styles.linkColumn}
							>
								<Stack spacing={1}>
									{vendor?.email !== null && (
										<Stack className="email" direction="row" spacing={1}>
											<Link className="iconLink" underline="always">
												<EmailOutlined />
												<span data-cy="seller-email">{vendor?.email}</span>
											</Link>

											<CopyableLink
												width="20px"
												height="20px"
												url={vendor?.email}
												aria-label={i18n.t(
													"SellerInformation.email.copyToClipboard",
													"Copy vendor email to clipboard"
												)}
												tabindex={0}
											/>
										</Stack>
									)}
									{vendor?.phone !== null && (
										<Link
											className="iconLink"
											underline="always"
											href={`tel:${vendor?.phone}`}
										>
											<PhoneOutlined />
											<span data-cy="seller-phone">{vendor?.phone}</span>
										</Link>
									)}
								</Stack>
							</Grid>
						</Grid>

						<Grid item xs={12} md={6} lg={5} sx={styles.linkColumn}>
							<Typography variant="h5" mb={1} as="h4">
								<Trans i18nKey="OrderDetails.otherLinks">Other Links</Trans>
							</Typography>
							<Stack spacing={1}>
								{pitchPage && (
									<Link
										className="iconLink "
										underline="always"
										color="primary"
										href={pitchPage}
										target="_blank"
										rel="noopener noreferrer"
									>
										<WebAssetOutlined />
										<Trans i18nKey="OrderDetails.visitSellerHomePage">
											Visit Seller's Homepage
										</Trans>
									</Link>
								)}
								{vendor?.supportSite && (
									<Link
										className="iconLink "
										underline="always"
										color="primary"
										href={vendor.supportSite}
										target="_blank"
										rel="noopener noreferrer"
									>
										<ScreenShareOutlined />
										<Trans i18nKey="OrderDetails.visitSellerSupportPage">
											Visit Seller's Support Page
										</Trans>
									</Link>
								)}
								{(state || countryCode) && (
									<>
										<Link
											component="button"
											className="iconLink"
											underline="always"
											color="primary"
											onClick={handleAddressModal}
										>
											<LocationOnOutlined />
											<Trans i18nKey="OrderDetails.viewSellerBusinessAddress">
												View Seller's Business Address
											</Trans>
										</Link>
										<Dialog
											open={addressModalOpen}
											onClose={handleAddressModal}
											maxWidth="sm"
											aria-labelledby="view-address-modal"
											aria-describedby="view-address"
										>
											<DialogTitle variant="h3">
												{address1 ? (
													<Trans i18nKey="OrderDetails.businessAddressFor">
														Business Address for
													</Trans>
												) : (
													<Trans i18nKey="OrderDetails.businessLocationFor">
														Business Location for
													</Trans>
												)}{" "}
												{customerService?.brand}
												<IconButton
													aria-label="close"
													onClick={handleAddressModal}
													sx={{
														position: "absolute",
														right: 8,
														top: 8,
														color: cbNeutral[700]
													}}
												>
													<Close />
												</IconButton>
											</DialogTitle>
											<DialogContent sx={styles.addressDialogContent}>
												<Box margin="0 0 20px">
													{!residential && address1 ? (
														<>
															<Typography>{address1}</Typography>
															{address2 && (
																<Typography>
																	{address2} <br />
																</Typography>
															)}
															<Typography>
																{customerService?.city}, {state}
																<br />
																{customerService?.zip},{" "}
																{countryCode}
															</Typography>
														</>
													) : (
														<Typography>
															{state}, {countryCode}
														</Typography>
													)}

													<Typography
														variant="subtitle1"
														sx={{
															padding: "1rem 0 0 1rem",
															textIndent: "-1rem",
															lineHeight: 1.3,
															fontWeight: 600
														}}
													>
														**{" "}
														{(residential || !address1) && (
															<Trans i18nKey="SellerInformation.businessAddress.residential">
																There is no business address
																available for this Seller. All
																customer inquiries should be
																submitted to the seller by phone or
																email.
															</Trans>
														)}{" "}
														<Trans i18nKey="SellerInformation.businessAddress.pleaseReview">
															Please review the seller's return
															instructions before initiating a return.
														</Trans>
													</Typography>
												</Box>
											</DialogContent>
										</Dialog>
									</>
								)}
								<Link
									component="button"
									className="iconLink red"
									underline="always"
									sx={{ color: palette.error.main }}
									onClick={() =>
										window.open("https://www.clickbank.com/report-a-seller")
									}
								>
									<WarningAmber color="error" />
									<Trans i18nKey="OrderDetails.reportSeller">Report Seller</Trans>
								</Link>
							</Stack>
						</Grid>
					</Grid>
				);
			})}
		</div>
	);
};

SellerInformation.propTypes = {
	lineItems: PropTypes.arrayOf(PropTypes.object),
	pitchPage: PropTypes.string,
	receiptNo: PropTypes.string
};

export default SellerInformation;
